import { MultiSelect } from '@mantine/core';
import { Tag } from '@carbon/react';
import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useGetCategoriesByEntityQuery } from 'api/categoryEndpoints';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import { useEventContext } from 'Screens/Dashboard/Events/EventHandler/EntityEventContext';
import styles from './EventTypePicker.module.scss';

type Props = {
    showError?: boolean;
    readonly?: boolean;
};

const EventTypePicker = ({
    showError = false,
    readonly = false,
}: Props) => {
    const intl = useIntl();
    const { values, updateValues } = useEventContext();
    const { categoryIds } = values;

    const currentEntityId = useGetCurrentEntityId();
    const selectedEntityId = values.entityId ?? currentEntityId;
    const { data: categories } = useGetCategoriesByEntityQuery(selectedEntityId ?? '', {
        skip: !selectedEntityId,
    });

    const eventTypes = useMemo(() => (categories ?? []).map((ct) => ({
        value: ct.id,
        label: ct.name,
    })), [categories]);
    const selectedCategories = useMemo(
        () => (categories ?? []).filter((category) => categoryIds.includes(category.id)),
        [categories, categoryIds],
    );

    const onChange = (selectedValues: string[]) => {
        updateValues({
            ...values,
            categoryIds: selectedValues,
        });
    };

    if (readonly) {
        return (
            <div className={styles.container}>
                <div className={styles.readonlyLabel}>
                    {intl.formatMessage(messages.selectType)}
                </div>
                <div className={styles.readonlyTags}>
                    {selectedCategories.length > 0 ? selectedCategories.map((category) => (
                        <Tag
                            key={category.id}
                            className={styles.readonlyTag}
                            type="gray"
                        >
                            {category.name}
                        </Tag>
                    )) : (
                        <span className={styles.emptyValue}>-</span>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <MultiSelect
                id="event-type-picker"
                label={intl.formatMessage(messages.selectType)}
                data={eventTypes}
                value={categoryIds}
                onChange={onChange}
                error={showError && categoryIds.length === 0 ? intl.formatMessage(messages.required) : undefined}
                radius="md"
                size="md"
                searchable
                clearable
                required
                classNames={{
                    input: styles.input,
                    dropdown: styles.dropdown,
                    pill: styles.pill,
                }}
                comboboxProps={{ withinPortal: false, zIndex: 10000 }}
            />
        </div>

    );
};

const messages = defineMessages({
    selectType: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventTypePicker.EventTypePicker.selectType',
        defaultMessage: 'Kategoria wydarzenia',
    },
    required: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventTypePicker.EventTypePicker.required',
        defaultMessage: 'Wybierz kategorię wydarzenia',
    },
});

export default EventTypePicker;
